import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import MyButton from "../../../components/MyButton";
import MyTitle from "../../../components/MyTitle";
import MyLoading from "../../../components/MyLoading";
import FileItem from "../../../components/inputs/MyInputFile/FileItem";
import DeleteBoardItemDialog from "../../Common/DeleteBoardItemDialog";
import ResearchComment03 from "../Comment/ResearchComment03";
import IconEdit from "../../../components/Icons/IconEdit";
import IconSave from "../../../components/Icons/IconSave";
import MyDropdownSelect from "../../../components/inputs/MyDropdownSelect";

import { AuthContext } from "../../../context/AuthProvider";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { PopupContext } from "../../../context/PopupProvider";
import { POST_STATUS_IDEA } from "../../../constants";
import { getStatusLabel } from "../../../utils";
import { FormProvider, useForm } from "react-hook-form";
import { SiteContext } from "../../../context/SiteProvider";

const ResearchDetail03 = ({ boardCode, parent }) => {
  const navigate = useNavigate();
  const { useGetBoard, useGetBoardItem, updateBoardItemMutation } =
    useBoardItem();
  const { profile, isAuthenticated, isAdmin } = useContext(AuthContext);
  const { itemNo } = useParams();
  const { openPopup } = useContext(PopupContext);
  const { setLoadingScreen } = useContext(SiteContext);
  const queryClient = useQueryClient();

  const { data: board } = useGetBoard({ boardCode }, (data) => data.data);

  const [replyMode, setReplyMode] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const { data, isLoading, isError, error } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      includeAdjacent: true,
    },
    (data) => data.data
  );

  const statuses = useMemo(
    () =>
      board?.additionalItems?.length
        ? board.additionalItems
            .find((c) => c.code === "status")
            ?.dropDown.map((c) => ({
              value: c,
              label: POST_STATUS_IDEA[c],
            }))
        : [],
    [board]
  );

  const methods = useForm({
    defaultValues: {
      status: "waiting",
    },
  });

  const onSubmit = async (vals) => {
    if (isAdmin) {
      const payload = {
        title: data.title,
        body: data.body,
        additionalItems: {
          status: vals?.status,
        },
        attachedFiles: data?.attachedFiles?.map((file) => ({
          fileId: file.fileId,
          fileName: file.fileName,
        })),
        writerEmail: data.email,
        userId: data?.writer?.userId ? data.writer.userId : profile.userId,
      };

      setLoadingScreen(true);

      updateBoardItemMutation.mutate(
        {
          boardCode: board?.boardCode,
          itemNo,
          payload,
        },
        {
          onSuccess: (res) => {
            if (res) {
              alert("제안서의 상태가 변경되었습니다.");
              queryClient.invalidateQueries({
                queryKey: ["/board-item/{boardCode}/{itemNo}"],
              });
              setEditMode(false);
            } else {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            }
          },
          onError: () => {
            alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
          },
          onSettled: () => {
            setLoadingScreen(false);
          },
        }
      );
    }
  };

  const openDeleteDialog = () =>
    openPopup(
      <DeleteBoardItemDialog
        boardCode={board?.boardCode}
        itemNo={itemNo}
        onRedirect={() => {
          navigate(parent ? parent : "/Research/Idea");
        }}
      />
    );

  useEffect(() => {
    if (data && !isLoading) {
      methods.setValue("status", data.additionalItems?.status ?? "waiting");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  return (
    <div>
      {isLoading ? (
        <MyLoading className="h-[150px]" />
      ) : isError && !data ? (
        <p className="text-center text-lg text-gray-dark pt-10">
          {error?.data?.code === "NO_PERMISSION"
            ? "이 글은 비밀글입니다. 작성자 및 관리자만 열람하실 수 있습니다."
            : "이 게시물의 정보를 볼 수 없습니다."}
        </p>
      ) : (
        <>
          <MyTitle title="제안 신청 내용" className="mb-4" />

          <table className="w-full border-collapse border-t-2 border-primary">
            <tbody>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  등록일
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {dayjs(data?.createdDate).format("YYYY-MM-DD")}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  진행상태
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {!editMode ? (
                    <div className="flex items-center gap-3">
                      {getStatusLabel(
                        data?.additionalItems?.status,
                        "2xl:text-base text-sm",
                        POST_STATUS_IDEA
                      )}
                      {isAdmin && (
                        <button
                          className="text-gray-dark"
                          onClick={() => setEditMode(true)}
                        >
                          <IconEdit />
                        </button>
                      )}
                    </div>
                  ) : editMode && isAdmin ? (
                    <FormProvider {...methods}>
                      <form
                        className="flex items-center gap-3"
                        onSubmit={methods.handleSubmit(onSubmit)}
                      >
                        <MyDropdownSelect
                          id="status"
                          name="status"
                          options={statuses}
                          dense
                          defaultValues="reviewing"
                          style={{
                            width: 100,
                          }}
                          dropdownStyle={{
                            width: 180,
                          }}
                        />
                        <button type="submit" className="text-[#0072c6]">
                          <IconSave />
                        </button>
                      </form>
                    </FormProvider>
                  ) : null}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  연구과제명
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {data?.title}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  작성자
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {data?.writer?.name}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  접수자
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {data?.writer?.email}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  내용
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto whitespace-pre-wrap">
                  {data?.body}
                </td>
              </tr>
              <tr>
                <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                  첨부파일
                </th>
                <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                  {data?.attachedFiles?.length > 0 ? (
                    <div>
                      {data.attachedFiles.map((item) => (
                        <FileItem key={item.fileId} file={item} />
                      ))}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex items-center my-10 sm:gap-4 gap-2">
            <MyButton
              variant="contained"
              color="#111111"
              className="font-semibold mr-auto text-white sm:min-w-[120px]"
              onClick={() => navigate(parent ? parent : "/Research/Idea")}
            >
              목록보기
            </MyButton>

            {isAuthenticated &&
            ((profile?.userId === data?.writer?.userId &&
              data?.additionalItems?.status === "waiting") ||
              isAdmin) ? (
              <>
                <MyButton
                  variant="outlined"
                  className="sm:min-w-[120px]"
                  onClick={openDeleteDialog}
                >
                  <span className="font-semibold">삭제</span>
                </MyButton>
                <MyButton
                  variant="outlined"
                  className="sm:min-w-[120px]"
                  onClick={() => navigate(`${parent}/Edit/${data?.itemNo}`)}
                >
                  <span className="font-semibold">수정</span>
                </MyButton>
              </>
            ) : (
              <></>
            )}
            {isAdmin && !replyMode ? (
              <MyButton
                variant="contained"
                color="primary"
                className="sm:min-w-[120px] font-semibold"
                onClick={() => {
                  setReplyMode(true);
                  const form = document.getElementById("reply-form");

                  if (form) {
                    form.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
              >
                답변하기
              </MyButton>
            ) : (
              <></>
            )}
          </div>

          <ResearchComment03
            board={board}
            boardItem={data}
            className="mt-10"
            replyMode={replyMode}
            setReplyMode={setReplyMode}
          />
        </>
      )}
    </div>
  );
};

export default ResearchDetail03;
